// Web Speech API の認識セッションを管理するコントローラ。
// Chrome の SpeechRecognition は無音や時間経過で勝手に終了するため、
// ここのリスタート状態機械が長時間文字起こしの生命線になっている。
// 挙動を変更する場合は必ず実マイクでの長時間口述テストを行うこと。
import { TranscriptSegmenter } from "../lib/transcript/segments";
import { audioStreamService } from "./audioStreamService";

// Web Speech APIのSpeechRecognitionを型安全に扱うための定義
interface SpeechRecognition extends EventTarget {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	// Chrome 135+ (2025-04-01) からサポートされた start(track) オーバーロード
	start: (track?: MediaStreamTrack) => void;
	stop: () => void;
	abort: () => void;
	onresult: (event: SpeechRecognitionEvent) => void;
	onerror: (event: SpeechRecognitionErrorEvent) => void;
	onend: () => void;
}

interface SpeechRecognitionErrorEvent {
	error: string;
}

interface SpeechRecognitionEvent {
	resultIndex: number;
	results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
	length: number;
	item(index: number): SpeechRecognitionResult;
	[index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
	isFinal: boolean;
	length: number;
	item(index: number): SpeechRecognitionAlternative;
	[index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
	transcript: string;
	confidence: number;
}

declare global {
	interface Window {
		SpeechRecognition: unknown;
		webkitSpeechRecognition: unknown;
	}
}

// Web Speech APIのクロスブラウザ対応
const SpeechRecognitionAPI = (window.SpeechRecognition || window.webkitSpeechRecognition) as
	| (new (
			...args: unknown[]
	  ) => SpeechRecognition)
	| undefined;

export interface SpeechRecognitionCallbacks {
	/** タイムスタンプ付きの確定行（改行込み）が届いたとき */
	onFinalText: (text: string) => void;
	/** 中間結果が更新されたとき（空文字はクリア） */
	onInterimText: (text: string) => void;
	/** リスニング状態が変化したとき */
	onListeningChange: (listening: boolean) => void;
	/** エラーメッセージ（null は解消） */
	onError: (message: string | null) => void;
}

export interface RecognitionOptions {
	language: string;
	deviceId?: string;
}

const MAX_RESTART_ATTEMPTS = 10;
// QUIRK: onend から同期的に start() を呼ぶと InvalidStateError になるため約1秒待つ
const RESTART_DELAY_MS = 1000;
const RESTART_RESET_INTERVAL = 60000; // 1分経過で再起動カウンターをリセット
const GIVE_UP_RETRY_DELAY_MS = 60000; // 最大試行回数到達後、1分待って再試行
// QUIRK: Chrome の認識は長時間セッションで劣化・停止するため4分ごとに予防的に再起動する
const PERIODIC_RESTART_INTERVAL = 240000;
const SEGMENT_CLEAR_INTERVAL = 600000; // 10分ごとに重複排除キャッシュを点検

export class SpeechRecognitionController {
	private recognition: SpeechRecognition | null = null;
	// 自前で開いたマイクストリーム（リスタートのたびにリークしないよう必ず解放する）
	private ownedStream: MediaStream | null = null;
	readonly segmenter = new TranscriptSegmenter();

	private readonly callbacks: SpeechRecognitionCallbacks;
	private language = "ja-JP";
	private deviceId: string | undefined;

	/** ユーザー（セッション）が認識の継続を望んでいる状態 */
	private active = false;
	/** 認識が動作している（と見なしている）状態 */
	private listening = false;
	/** 多重起動防止ロック */
	private starting = false;

	private restartAttempts = 0;
	private lastRestartReset = Date.now();
	private restartTimeout: number | null = null;
	private giveUpTimeout: number | null = null;
	private periodicRestartInterval: number | null = null;
	private segmentClearInterval: number | null = null;

	constructor(callbacks: SpeechRecognitionCallbacks) {
		this.callbacks = callbacks;
	}

	get isListening(): boolean {
		return this.listening;
	}

	get isActive(): boolean {
		return this.active;
	}

	private setListening(value: boolean): void {
		this.listening = value;
		this.callbacks.onListeningChange(value);
	}

	/** 認識を開始する。既に開始中なら何もしない */
	async start(options: RecognitionOptions): Promise<void> {
		this.language = options.language;
		this.deviceId = options.deviceId;
		this.active = true;

		if (this.starting) return;
		this.starting = true;
		try {
			await this.startInternal();
		} finally {
			this.starting = false;
		}
		this.startMaintenanceTimers();
	}

	/**
	 * 認識の停止を要求するが、ハンドラは維持する。
	 * recognition.stop() の後に遅延して届く最終認識結果（final）を受け取るために使う。
	 * フラッシュ待ちの後、必ず stop() を呼んで完全に片付けること。
	 */
	requestStop(): void {
		this.active = false;

		if (this.restartTimeout !== null) {
			window.clearTimeout(this.restartTimeout);
			this.restartTimeout = null;
		}
		this.stopMaintenanceTimers();

		try {
			this.recognition?.stop();
		} catch {
			// 停止中のエラーは無視
		}
	}

	/** 認識を停止する（ユーザー操作・録音停止時） */
	stop(): void {
		this.active = false;

		// 予約済みのリスタートを解除
		if (this.restartTimeout !== null) {
			window.clearTimeout(this.restartTimeout);
			this.restartTimeout = null;
		}
		this.stopMaintenanceTimers();

		if (this.recognition) {
			// QUIRK: ハンドラをクリアしてから止める。クリアしないと旧インスタンスの
			// onend が後から発火して勝手に再起動する（ゾンビインスタンス）
			this.discardRecognition();
			this.setListening(false);

			// 中間テキストをクリア
			this.callbacks.onInterimText("");
		}

		this.releaseOwnedStream();
	}

	/** 完全に破棄する（アンマウント時）。全タイマーを止める */
	dispose(): void {
		this.stop();
		if (this.giveUpTimeout !== null) {
			window.clearTimeout(this.giveUpTimeout);
			this.giveUpTimeout = null;
		}
	}

	// ---- 内部実装 ----

	private discardRecognition(): void {
		if (!this.recognition) return;
		try {
			this.recognition.onresult = () => {};
			this.recognition.onerror = () => {};
			this.recognition.onend = () => {};
			this.recognition.stop();
		} catch {
			// 停止中のエラーは無視
		}
		this.recognition = null;
	}

	private releaseOwnedStream(): void {
		if (this.ownedStream) {
			for (const track of this.ownedStream.getTracks()) {
				track.stop();
			}
			this.ownedStream = null;
		}
	}

	private startMaintenanceTimers(): void {
		if (this.periodicRestartInterval === null) {
			// QUIRK: 4分ごとの予防的リスタート
			this.periodicRestartInterval = window.setInterval(() => {
				if (this.recognition && this.active && this.listening) {
					// ロックが張り付いていても強制的に再起動する（原実装の挙動）
					this.starting = true;
					this.discardRecognition();

					window.setTimeout(() => {
						this.startInternal().finally(() => {
							this.starting = false;
						});
					}, RESTART_DELAY_MS);
				}
			}, PERIODIC_RESTART_INTERVAL);
		}

		if (this.segmentClearInterval === null) {
			this.segmentClearInterval = window.setInterval(() => {
				this.segmenter.clearSegmentsIfOverCap();
			}, SEGMENT_CLEAR_INTERVAL);
		}
	}

	private stopMaintenanceTimers(): void {
		if (this.periodicRestartInterval !== null) {
			window.clearInterval(this.periodicRestartInterval);
			this.periodicRestartInterval = null;
		}
		if (this.segmentClearInterval !== null) {
			window.clearInterval(this.segmentClearInterval);
			this.segmentClearInterval = null;
		}
	}

	private async startInternal(): Promise<void> {
		try {
			// 既に起動中なら何もしない
			if (this.listening && this.recognition) {
				return;
			}

			// 既存のインスタンスがある場合は停止して破棄
			this.discardRecognition();

			if (!SpeechRecognitionAPI) {
				this.callbacks.onError("お使いのブラウザは音声認識をサポートしていません");
				return;
			}

			// QUIRK: listening を先に立てて、onend による即時再起動を防止
			this.setListening(true);

			const recognition = new SpeechRecognitionAPI();
			this.recognition = recognition;
			recognition.lang = this.language;
			recognition.continuous = true;
			recognition.interimResults = true;

			// 選択されたマイクがある場合、そのマイクからMediaStreamTrackを取得
			let audioTrack: MediaStreamTrack | null = null;
			if (this.deviceId) {
				// 録音セッションの共有ストリームがあればそれを使う
				const sharedStream = audioStreamService.getCurrentStream();
				if (sharedStream && sharedStream.getAudioTracks().length > 0) {
					audioTrack = sharedStream.getAudioTracks()[0];
				} else {
					try {
						this.releaseOwnedStream();
						const stream = await navigator.mediaDevices.getUserMedia({
							audio: { deviceId: { exact: this.deviceId } },
						});
						this.ownedStream = stream;
						if (stream.getAudioTracks().length > 0) {
							audioTrack = stream.getAudioTracks()[0];
						}
					} catch (error) {
						const err = error as Error;
						console.warn(`選択されたマイク (${this.deviceId}) のストリームを開けませんでした:`, err);
						this.callbacks.onError(`選択されたマイクにアクセスできません: ${err.message || String(err)}`);
						return;
					}
				}
			}

			recognition.onresult = (event: SpeechRecognitionEvent) => {
				let interimTranscript = "";
				let finalTranscript = "";

				for (let i = event.resultIndex; i < event.results.length; i++) {
					const transcript = event.results[i][0].transcript.trim();

					// 空の認識結果は無視
					if (!transcript) {
						continue;
					}

					if (event.results[i].isFinal) {
						// QUIRK: 重複排除とタイムスタンプ付与は segmenter に集約
						finalTranscript = this.segmenter.processFinal(transcript, Date.now());
					} else {
						// QUIRK: 最初の interim が届いた時点でセグメント開始時刻をアンカーする
						// （final 時ではなく発話開始時点をタイムスタンプにするため）
						this.segmenter.noteInterim(Date.now());
						interimTranscript += transcript;
					}
				}

				this.callbacks.onInterimText(interimTranscript);

				if (finalTranscript) {
					this.callbacks.onFinalText(`${finalTranscript}\n`);
				}
			};

			recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
				// QUIRK: no-speech は自然な無音で頻発するため、エラーとして扱わない
				if (event.error !== 'no-speech') {
					console.error("音声認識エラー:", event.error);
				}

				// network エラーと no-speech エラーは自動復旧を試みる
				if (event.error === 'network' || event.error === 'no-speech') {
					// no-speechの場合はエラー表示しない
					if (event.error === 'network') {
						this.callbacks.onError(`音声認識エラー: ${event.error} - 自動復旧を試行中...`);
						window.setTimeout(() => this.callbacks.onError(null), 3000);
					}

					// QUIRK: no-speech では listening が落ちることがあるため、
					// abort() で明示的に終了させ、listening を立て直して onend の再起動を有効にする
					if (this.active && !this.starting) {
						try {
							recognition.abort();
						} catch {
							// abort時のエラーは無視
						}
						this.setListening(true);
					}
				} else {
					this.callbacks.onError(`音声認識エラー: ${event.error}`);
				}
			};

			// QUIRK: Chrome の認識は無音や約60秒で勝手に終了する。
			// この onend リスタート機構が長時間文字起こしを成立させている。
			recognition.onend = () => {
				// ロックが張り付いていたらリセット（原実装の挙動）
				if (this.starting) {
					this.starting = false;
				}

				if (this.active && this.listening) {
					// 既存のタイムアウトがあればクリア
					if (this.restartTimeout !== null) {
						window.clearTimeout(this.restartTimeout);
					}

					// 前回のリセットから一定時間経過していたらカウンターをリセット
					const now = Date.now();
					if (now - this.lastRestartReset > RESTART_RESET_INTERVAL) {
						this.restartAttempts = 0;
						this.lastRestartReset = now;
					}

					this.restartAttempts += 1;

					if (this.restartAttempts <= MAX_RESTART_ATTEMPTS) {
						// QUIRK: 同期的に start() を呼ぶと InvalidStateError になるため遅延させる
						this.restartTimeout = window.setTimeout(() => {
							try {
								if (this.active && this.listening && !this.starting) {
									// QUIRK: start(track) が使える場合は選択マイクのトラックで再開する
									if (audioTrack && "start" in recognition && typeof recognition.start === "function") {
										try {
											recognition.start(audioTrack);
										} catch (trackError) {
											console.warn("トラック付き起動が失敗、通常起動にフォールバック:", trackError);
											recognition.start();
										}
									} else {
										recognition.start();
									}

									// 成功したら最後のリセット時刻を更新
									this.lastRestartReset = Date.now();
								} else if (!this.active) {
									this.setListening(false);
								}
							} catch (err) {
								console.error("音声認識の再起動に失敗:", err);
								this.setListening(false);
							}
							this.restartTimeout = null;
						}, RESTART_DELAY_MS);
					} else {
						console.warn(`最大再起動試行回数(${MAX_RESTART_ATTEMPTS})に達しました`);

						// 1分待ってから再度試行可能にする（dispose でキャンセルされる）
						this.giveUpTimeout = window.setTimeout(() => {
							this.restartAttempts = 0;
							this.lastRestartReset = Date.now();

							if (this.active && !this.starting) {
								this.starting = true;
								this.startInternal().finally(() => {
									this.starting = false;
								});
							}
							this.giveUpTimeout = null;
						}, GIVE_UP_RETRY_DELAY_MS);

						this.setListening(false);
						this.callbacks.onError("音声認識が一時的に停止しました。自動的に再開を試みます。");
					}
				} else {
					// 認識の継続を望んでいない場合はリスニング状態を解除
					if (!this.active) {
						this.setListening(false);
					}
				}
			};

			// QUIRK: start(track) は Chrome 135+ のみ。Function.length で引数対応を検出する
			//（選択マイクを認識に反映できる唯一の手段）
			try {
				if (audioTrack) {
					const supportsTrack =
						typeof recognition.start === "function" &&
						"start" in recognition &&
						recognition.start.length > 0;

					if (supportsTrack) {
						try {
							(recognition.start as (track?: MediaStreamTrack) => void)(audioTrack);
						} catch (trackError) {
							console.warn("トラックを使った起動が失敗:", trackError);
							recognition.start();
						}
					} else {
						recognition.start();
					}
				} else {
					recognition.start();
				}

				this.setListening(true);
				this.callbacks.onError(null);

				// 状態をリセット
				this.segmenter.clearSegments();
				this.restartAttempts = 0;
			} catch (startError) {
				console.error("音声認識の起動に失敗:", startError);

				// start(track) が使えない場合のフォールバック
				if (audioTrack) {
					try {
						recognition.start();
						this.setListening(true);
						this.callbacks.onError(null);

						this.segmenter.clearSegments();
						this.restartAttempts = 0;
					} catch (fallbackError) {
						console.error("フォールバックも失敗:", fallbackError);
						this.callbacks.onError("音声認識を開始できませんでした");
					}
				} else {
					this.callbacks.onError("音声認識を開始できませんでした");
				}
			}

			// QUIRK: 録音開始時刻はリスタートを跨いで維持される（初回のみ記録）
			this.segmenter.markRecordingStarted(Date.now());
		} catch (err) {
			console.error("音声認識の開始に失敗しました:", err);
			this.callbacks.onError("音声認識の開始に失敗しました");
		}
	}
}
